import { useContext, useCallback } from 'react';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import events from 'utilities/amplitude/events';
import formatAmplitudeError from 'utilities/amplitude/helpers';

const invoiceEvents = events.epic.Invoices;

export type InvoiceButtonLocation = 'Confirmation toast' | 'Detail View menu';
export type InvoiceEventResult = 'Success' | 'Error';

const STEP_LABEL_MAP: Record<string, string> = {
  'Invoice Details': 'Step 1',
  'Select Products': 'Step 2',
  Notes: 'Step 3'
};

const getStepLabel = (stepName?: string): string =>
  stepName ? STEP_LABEL_MAP[stepName] || stepName : 'Step 1';

const formatResultWithDuration = (
  result: InvoiceEventResult,
  apiCallLengthMs: number
): string => `${result} (${apiCallLengthMs} ms)`;

const formatErrorProperty = (
  result: InvoiceEventResult,
  error?: unknown
): string =>
  result === 'Error' && error ? formatAmplitudeError(error) : 'N/A';

const useInvoiceEvents = () => {
  const { sendEventToAmplitude } = useContext(AmplitudeContext);

  const sendAccessEvent = useCallback(() => {
    sendEventToAmplitude?.(invoiceEvents.accessInvoice);
  }, [sendEventToAmplitude]);

  const sendInitiateCreateEvent = useCallback(() => {
    sendEventToAmplitude?.(invoiceEvents.initiateCreateInvoice);
  }, [sendEventToAmplitude]);

  const sendApplyFiltersEvent = useCallback(
    (invoiceDateRangeAdjusted: boolean) => {
      sendEventToAmplitude?.(invoiceEvents.applyInvoiceFilters, {
        'Invoice Date range adjusted': invoiceDateRangeAdjusted
      });
    },
    [sendEventToAmplitude]
  );

  const sendViewInvoiceEvent = useCallback(() => {
    sendEventToAmplitude?.(invoiceEvents.viewInvoice);
  }, [sendEventToAmplitude]);

  const sendInitiateEditEvent = useCallback(() => {
    sendEventToAmplitude?.(invoiceEvents.initiateEditInvoice);
  }, [sendEventToAmplitude]);

  const sendSaveInvoiceEvent = useCallback(
    (
      result: InvoiceEventResult,
      apiCallLengthMs: number,
      numberOfProducts: number,
      isDuplicateFlow: boolean,
      error?: unknown
    ) => {
      sendEventToAmplitude?.(invoiceEvents.saveInvoice, {
        'Save Invoice result': formatResultWithDuration(
          result,
          apiCallLengthMs
        ),
        Error: formatErrorProperty(result, error),
        'Number of products': numberOfProducts,
        'Record source': isDuplicateFlow ? 'Invoice' : 'N/A'
      });
    },
    [sendEventToAmplitude]
  );

  const sendSaveInvoiceEditsEvent = useCallback(
    (
      result: InvoiceEventResult,
      apiCallLengthMs: number,
      error?: unknown,
      stepName?: string
    ) => {
      sendEventToAmplitude?.(invoiceEvents.saveInvoiceEdits, {
        'Save Invoice result': formatResultWithDuration(
          result,
          apiCallLengthMs
        ),
        Error: formatErrorProperty(result, error),
        'Save from step': getStepLabel(stepName)
      });
    },
    [sendEventToAmplitude]
  );

  const sendDeleteEvent = useCallback(
    (result: InvoiceEventResult, apiCallLengthMs: number, error?: unknown) => {
      sendEventToAmplitude?.(invoiceEvents.deleteInvoice, {
        'Delete Invoice result': formatResultWithDuration(
          result,
          apiCallLengthMs
        ),
        Error: formatErrorProperty(result, error)
      });
    },
    [sendEventToAmplitude]
  );

  const sendInitiateDuplicateEvent = useCallback(
    (buttonLocation: InvoiceButtonLocation) => {
      sendEventToAmplitude?.(invoiceEvents.initiateDuplicateInvoice, {
        'Button location': buttonLocation
      });
    },
    [sendEventToAmplitude]
  );

  const sendDownloadPdfEvent = useCallback(
    (buttonLocation: InvoiceButtonLocation) => {
      sendEventToAmplitude?.(invoiceEvents.downloadInvoicePDF, {
        'Button location': buttonLocation
      });
    },
    [sendEventToAmplitude]
  );

  return {
    sendAccessEvent,
    sendInitiateCreateEvent,
    sendApplyFiltersEvent,
    sendViewInvoiceEvent,
    sendInitiateEditEvent,
    sendSaveInvoiceEvent,
    sendSaveInvoiceEditsEvent,
    sendDeleteEvent,
    sendInitiateDuplicateEvent,
    sendDownloadPdfEvent
  };
};

export default useInvoiceEvents;
